
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useRef, Suspense, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { 
    PresentationControls, 
    Stage, 
    Float, 
    Environment, 
    RoundedBox
} from '@react-three/drei';
import * as THREE from 'three';

// Constants for card design
const GHOST_LOGO_URL = "https://raw.githubusercontent.com/ghost-protocol-labs/ghost-token-sui/refs/heads/main/assets/GHOST.svg";

// @google/genai-fix: Local aliases for Three.js elements to avoid intrinsic JSX type issues.
const MeshStandardMaterial = 'meshStandardMaterial' as any;
const Group = 'group' as any;

const Card3D = ({ holderName, cardNumber }: { holderName: string, cardNumber: string }) => {
    const cardRef = useRef<THREE.Group>(null);
    
    // Create a high-resolution canvas-based texture for the card face
    const texture = useMemo(() => {
        const canvas = document.createElement('canvas');
        canvas.width = 2048; // Higher res for detail
        canvas.height = 1292;
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;

        // 1. Deep Obsidian Base
        ctx.fillStyle = '#0A0B0F';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // 2. Subtle Brushed Metal Gradient
        const grad = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
        grad.addColorStop(0, 'rgba(255,255,255,0.08)');
        grad.addColorStop(0.3, 'rgba(0,0,0,0)');
        grad.addColorStop(0.7, 'rgba(0,0,0,0)');
        grad.addColorStop(1, 'rgba(255,255,255,0.04)');
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // 3. Realistic Typography (Laser Etched Feel)
        ctx.textAlign = 'left';
        
        // Card Number
        ctx.fillStyle = 'rgba(255,255,255,0.95)';
        ctx.font = 'bold 92px Courier New, monospace';
        ctx.letterSpacing = '24px';
        ctx.fillText(cardNumber, 160, 800);

        // Labels
        ctx.letterSpacing = '8px';
        ctx.fillStyle = 'rgba(255,255,255,0.3)';
        ctx.font = 'bold 36px Inter, sans-serif';
        ctx.fillText('AUTHORIZED HOLDER', 160, 1020);
        
        // Name
        ctx.letterSpacing = '4px';
        ctx.fillStyle = 'white';
        ctx.font = 'bold 64px Inter, sans-serif';
        ctx.fillText(holderName.toUpperCase(), 160, 1110);

        // Suffix Branding
        ctx.textAlign = 'right';
        ctx.font = 'italic 600 84px Playfair Display, serif';
        ctx.fillText('Ghost', 1880, 1110);
        
        ctx.font = 'bold 24px Inter, sans-serif';
        ctx.letterSpacing = '6px';
        ctx.fillStyle = 'rgba(197, 160, 89, 0.6)';
        ctx.fillText('TITANIUM ELITE', 1880, 1160);

        // 4. Logo watermark
        const img = new Image();
        img.src = GHOST_LOGO_URL;
        img.crossOrigin = "anonymous";
        img.onload = () => {
            ctx.save();
            ctx.globalAlpha = 0.02;
            ctx.translate(1600, 800);
            ctx.rotate(Math.PI / 8);
            ctx.drawImage(img, -400, -400, 800, 800);
            ctx.restore();
        };

        const tex = new THREE.CanvasTexture(canvas);
        tex.anisotropy = 16;
        return tex;
    }, [holderName, cardNumber]);

    return (
        <Group ref={cardRef}>
            {/* Main Card Body - Titanium PBR Material */}
            <RoundedBox args={[8.5, 5.4, 0.18]} radius={0.4} smoothness={8}>
                <MeshStandardMaterial 
                    map={texture}
                    metalness={0.9}
                    roughness={0.15}
                    envMapIntensity={1.5}
                />
            </RoundedBox>

            {/* High Fidelity Chip */}
            <RoundedBox args={[1.2, 0.85, 0.06]} radius={0.1} position={[-2.8, 1.3, 0.1]}>
                <MeshStandardMaterial 
                    color="#C5A059"
                    metalness={1}
                    roughness={0.2}
                    emissive="#C5A059"
                    emissiveIntensity={0.1}
                />
            </RoundedBox>
            
            {/* Contactless Icon Detail */}
            <RoundedBox args={[0.4, 0.6, 0.02]} radius={0.02} position={[3.4, 1.4, 0.1]}>
                 <MeshStandardMaterial color="white" opacity={0.2} transparent />
            </RoundedBox>

            {/* Back Strip & Detail */}
            <mesh position={[0, 0, -0.1]} rotation={[0, Math.PI, 0]}>
                <planeGeometry args={[8.5, 5.4]} />
                <meshStandardMaterial color="#050505" metalness={1} roughness={0.05} />
            </mesh>
            <RoundedBox args={[8.5, 0.8, 0.02]} radius={0} position={[0, 1, -0.11]} rotation={[0, Math.PI, 0]}>
                <meshBasicMaterial color="#000" />
            </RoundedBox>
        </Group>
    );
};

export const CreditCardMockup: React.FC<{ 
    holderName?: string; 
    cardNumber?: string; 
}> = ({ 
    holderName = "ALEXANDER GHOST", 
    cardNumber = "4592 1029 3847 9201"
}) => {
    return (
        <div className="w-full h-[350px] sm:h-[500px] relative cursor-grab active:cursor-grabbing">
            {/* Interactive hint */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 opacity-40">
                <div className="text-[9px] font-bold uppercase tracking-[0.4em] text-stone-500">3D Photorealistic Engine</div>
                <div className="w-4 h-4 rounded-full border border-stone-400 flex items-center justify-center">
                    <RefreshCw size={8} className="animate-spin-slow" />
                </div>
            </div>

            <Canvas shadows camera={{ position: [0, 0, 10], fov: 32 }}>
                <Suspense fallback={null}>
                    <Stage environment="studio" intensity={0.6} contactShadow={{ opacity: 0.5, blur: 3 }} adjustCamera={true}>
                        <PresentationControls
                            global
                            config={{ mass: 1, tension: 200, friction: 20 }}
                            snap={{ mass: 2, tension: 400 }}
                            rotation={[0, 0.2, 0]}
                            polar={[-Math.PI / 4, Math.PI / 4]}
                            azimuth={[-Math.PI / 2, Math.PI / 2]}
                        >
                            <Float speed={2.5} rotationIntensity={0.2} floatIntensity={0.2}>
                                <Card3D holderName={holderName} cardNumber={cardNumber} />
                            </Float>
                        </PresentationControls>
                    </Stage>
                </Suspense>
                <Environment preset="night" />
            </Canvas>

            {/* Finish details */}
            <div className="absolute bottom-8 left-0 right-0 flex justify-center gap-16 pointer-events-none px-6">
                <div className="text-center">
                    <div className="text-[8px] font-black uppercase text-stone-500 tracking-[0.3em] mb-1.5">Surface</div>
                    <div className="text-[11px] font-bold text-[#0A0B0F] dark:text-white uppercase tracking-widest">Matte Obsidian</div>
                </div>
                <div className="text-center">
                    <div className="text-[8px] font-black uppercase text-stone-500 tracking-[0.3em] mb-1.5">Material</div>
                    <div className="text-[11px] font-bold text-[#0A0B0F] dark:text-white uppercase tracking-widest">G2 Titanium</div>
                </div>
                <div className="hidden sm:block text-center">
                    <div className="text-[8px] font-black uppercase text-stone-500 tracking-[0.3em] mb-1.5">Security</div>
                    <div className="text-[11px] font-bold text-emerald-500 uppercase tracking-widest">GhostOS Enclave</div>
                </div>
            </div>

            <style>
                {`
                @keyframes spin-slow {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                .animate-spin-slow {
                    animation: spin-slow 12s linear infinite;
                }
                `}
            </style>
        </div>
    );
};

const RefreshCw = ({ size, className }: { size: number, className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M3 21v-5h5"/></svg>
);
